import debounce from "debounce";

class DropdownMenu {
	container: HTMLElement|null = null;
	navigation: HTMLElement|null = null;
	currentMenuItem: HTMLLIElement|null = null;
	currentDropdown: Dropdown|null = null;
	menuItems: NodeListOf<HTMLLIElement>
	parentMenuItems: NodeListOf<HTMLLIElement>;
	dropdowns: Dropdown[] = [];
	openDelay: number = 150;

	constructor(navigation: HTMLElement, container: HTMLElement) {
		this.navigation = navigation;
		this.container = container;
		this.menuItems = navigation.querySelectorAll("li");
		this.parentMenuItems = navigation.querySelectorAll("li[class*=children]");

		navigation.querySelectorAll("ul.dropdown")
			.forEach(ul => {
				this.dropdowns.push(new Dropdown(<HTMLUListElement>ul));
			});

		if (this.dropdowns.length > 0) {
			this.addHandlers();
		}
	}

	addHandlers(): void {
		this.menuItems.forEach(item => {
			item.addEventListener("mouseenter", debounce(this.onMenuItemMouseOver.bind(this)));
			item.addEventListener("mouseleave", debounce(this.onMenuItemMouseOut.bind(this)));
		});

		this.parentMenuItems.forEach(item => {
			item.addEventListener("focusin", debounce(this.onMenuItemMouseOver.bind(this)));
			item.addEventListener("focusout", debounce(this.onMenuItemMouseOut.bind(this)));
			item.addEventListener("touchstart", this.onMenuItemTouch.bind(this), {passive: true});
			item.querySelector("a")?.addEventListener("mouseenter", debounce(this.onMenuItemLinkMouseOver.bind(this)));
		});

		this.container?.addEventListener("mouseleave", debounce(this.onContainerMouseOut.bind(this)));
	}

	getLevel(ul: HTMLUListElement|null): number {
		if (!ul) {
			return -1;
		}

		for (let key of ul.classList) {
			if (key.match(/lvl-\d/)) {
				return parseInt(key.replace("lvl-", ""));
			}
		}

		return -1;
	}

	hasDropdown(li: HTMLLIElement): boolean {
		return this.getDropdown(li) !== null;
	}

	getDropdown(li: HTMLLIElement): Dropdown|null {
		let out = null;
		this.dropdowns.forEach(item => {
			if (item.element.isEqualNode(li.querySelector(".dropdown"))) {
				out = item;
			}
		});
		return out;
	}

	getParentDropdowns(li: HTMLLIElement): Dropdown[] {
		let out: Dropdown[] = [];
		const ul = li.parentElement;
		if (ul instanceof HTMLUListElement) {
			this.dropdowns.forEach(item => {
				if (item.element.isEqualNode(ul) && ul.parentElement instanceof HTMLLIElement) {
					out.push(item);
					out = out.concat(this.getParentDropdowns(ul.parentElement));
				}
			});
		}

		return out;
	}

	collapseDropdowns() {
		const collapsible: Dropdown[] = [];
		this.dropdowns.forEach(item => {
			item.isCollapsible = item.isActive;

			if (item.isCollapsible) {
				if (this.currentDropdown && this.currentDropdown === item) {
					item.isCollapsible = false;
				}

				if (this.currentMenuItem && this.getParentDropdowns(this.currentMenuItem).indexOf(item) > -1) {
					item.isCollapsible = false;
				}

				if (this.currentMenuItem && this.getDropdown(this.currentMenuItem) === item) {
					item.isCollapsible = false;
				}
			}

			if (item.isCollapsible) {
				collapsible.push(item);
			}
		});

		collapsible.forEach(item => {
			if (item.isCollapsible) {
				item.hide();
			}
		});
	}

	onMenuItemExpanded(e: Event) {
		const item = <HTMLLIElement>e.target;
		item.querySelector("a")?.setAttribute("aria-expanded", "true");
	}

	/**
	 * @param {Event} e
	 */
	onMenuItemCollapsed(e: Event) {
		const item = <HTMLLIElement>e.target;
		item.querySelector("a")?.setAttribute("aria-expanded", "false");
	}

	onMenuItemMouseOver(e: Event) {
		const item = <HTMLLIElement>e.target;

		if (item instanceof HTMLLIElement) {
			this.currentMenuItem = item;
			this.currentDropdown = this.getDropdown(this.currentMenuItem);

			if (this.currentDropdown) {
				this.currentDropdown.show();
			}
		}
	}

	onMenuItemMouseOut(e: Event) {
		this.currentMenuItem = null;

		window.setTimeout(this.collapseDropdowns.bind(this), Dropdown.showDelay);
	}

	onContainerMouseOut(e: Event) {
		this.currentMenuItem = null;
		this.currentDropdown = null;

		this.collapseDropdowns();
	}

	onMenuItemTouch(e: TouchEvent) {
		let item = <HTMLLIElement>e.target;
		if (!(item instanceof HTMLLIElement)) {
			item = <HTMLLIElement>(<HTMLElement>e.target).closest("li");
		}
		const submenu = this.getDropdown(item);

		this.currentMenuItem = item;

		if (submenu) {
			if (!submenu.isActive) {
				e.preventDefault();
			}

			this.currentDropdown = submenu;
			this.currentDropdown.show();
			this.collapseDropdowns();
		}
	}

	onMenuItemLinkMouseOver(e: Event) {
		const a = <HTMLLinkElement>e.target;
		if (a.parentElement instanceof HTMLLIElement) {
			this.currentMenuItem = a.parentElement;
		}
	}
}

class Dropdown {
	element: HTMLUListElement;
	container: HTMLLIElement;
	isActive: boolean = false;
	isTransitioning: boolean = false;
	isCollapsible: boolean = true;
	className: string = "dropdown";
	activeClassName: string = "active";
	activeContainerClassName: string = "has-active-dropdown";
	showTimeout: number = 0;
	hideTimeout: number = 0;

	static showDelay: number = 150;
	static hideDelay: number = 250;

	constructor(element: HTMLUListElement) {
		this.element = element;
		this.container = <HTMLLIElement>element.parentElement;

		this.addParentItemIcons();
	}


	toggle(show: boolean) {
		if (show) {
			this.show();
		}
		else {
			this.hide();
		}
	}

	show() {
		window.clearTimeout(this.hideTimeout);
		this.isTransitioning = true;
		this.isActive = true;

		this.showTimeout = window.setTimeout(() => {
			this.element.classList.add(this.activeClassName);
			this.container.classList.add(this.activeContainerClassName);
			this.isTransitioning = false;
		}, Dropdown.showDelay);
	}

	hide() {
		window.clearTimeout(this.showTimeout);
		this.isActive = false;
		this.isTransitioning = true;

		this.hideTimeout = window.setTimeout(() => {
			this.element.classList.remove(this.activeClassName);
			this.container.classList.remove(this.activeContainerClassName);
			this.isTransitioning = false;
		}, Dropdown.hideDelay);
	}

	protected addParentItemIcons(): void {
		this.element.querySelectorAll("li[class*=children] > a")
			.forEach(i => {
				const icon = document.createElement("i");
				icon.classList.add("fal", "fa-angle-down");

				i.append(icon);
			});
	}
}

export default DropdownMenu;
